import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from '@pepper/utils/src/function/axiosInstance'
import { useGetTeamMembers } from '@pepper/ui'
import { useSelectedCompanyId } from 'utils/functions/misc'

import { useGetWorkflowStatus } from './useGetWorkflowStatus'

const getWorkflowLog = async ({
  itemId,
  isPvtDoc,
}: {
  itemId: string
  isPvtDoc: boolean
}) => {
  let response
  if (isPvtDoc) {
    response = await axios.get(`items/${itemId}/workflow-activity-log`)
  } else {
    response = await axios.get(`assignments/${itemId}/workflow-activity-log`)
  }
  return response.data as WorkflowLogType
}

export function useGetWorkflowLog({
  itemId,
  isPvtDoc,
}: {
  itemId: string
  isPvtDoc: boolean
}) {
  const companyId = useSelectedCompanyId()
  const { data: teamMembers } = useGetTeamMembers({ companyId })
  const { data: workflowStatuses } = useGetWorkflowStatus({ companyId })

  const _normalize = (array: any[]) =>
    array.reduce(
      (res, el) => ({
        ...res,
        [el.id]: el,
      }),
      {}
    )

  const { data } = useQuery({
    queryKey: ['workflow-log', itemId],
    queryFn: () => getWorkflowLog({ itemId, isPvtDoc }),
  })

  const normalizedTeamMembers = _normalize(teamMembers ?? [])
  const normalizedStatuses = _normalize(workflowStatuses ?? [])
  const normalizedLogAssignees = _normalize(data?.users ?? [])
  const normalizedLogStatuses = _normalize(data?.workflow_statuses ?? [])

  const results = data?.data.map(log => {
    return {
      ...log,
      assignee:
        log.key === 'workflow_assignee_id'
          ? {
              current: {
                ...normalizedLogAssignees[log.new_value],
                isDeleted: !normalizedTeamMembers[log.new_value],
              },
              previous: {
                ...normalizedLogAssignees[log.old_value],
                isDeleted: !normalizedTeamMembers[log.old_value],
              },
            }
          : undefined,
      status:
        log.key === 'workflow_status_id'
          ? {
              current: {
                ...normalizedLogStatuses[log.new_value],
                isDeleted: !normalizedStatuses[log.new_value],
              },
              previous: {
                ...normalizedLogStatuses[log.old_value],
                isDeleted: !normalizedStatuses[log.old_value],
              },
            }
          : undefined,
      updated_by: normalizedLogAssignees[log.updated_by],
    }
  })

  return {
    data: results,
  }
}
